/**
 * What a gesture at the Desktop surface should do — decided with no DOM.
 *
 * The rule this exists for is the one in `desktopPanelState.ts`'s header: an
 * agent is working inside this display, so a stray click from someone who
 * believes they are only watching is a real edit to a real machine.
 *
 * That rule used to live as five separate `if (!controlling) return;` lines
 * inside DesktopPanel's event callbacks. Five copies is five chances for the
 * next gesture to be added without one, and reaching any of them from a test
 * needs a real pointer against a mounted component — which is why it went
 * untested for as long as it did. Here it is a single expression, asserted
 * directly, and adding a gesture means extending one switch that is already
 * behind the guard.
 *
 * The panel keeps the DOM half: read the event, map the point, then apply what
 * this returns.
 */
import type { ComputerCommand } from "@cmmd-forge/contracts";

import { commandForPointerRelease, type DisplayPoint, scrollCommandFor } from "./desktopPanelState";

export type MouseButton = "left" | "middle" | "right";

/**
 * A gesture with its coordinates already mapped to the runtime display.
 *
 * `point: null` means the pointer was in the letterbox bars, where no display
 * pixel exists — the panel maps it, this decides what that means.
 */
export type DesktopGesture =
  | { readonly kind: "pointer_down"; readonly point: DisplayPoint | null }
  | {
      readonly kind: "pointer_up";
      readonly origin: DisplayPoint | null;
      readonly point: DisplayPoint | null;
      readonly button: MouseButton | null;
    }
  | { readonly kind: "double_click"; readonly point: DisplayPoint | null }
  | { readonly kind: "context_menu" }
  | {
      readonly kind: "key_down";
      readonly key: string;
      readonly command: ComputerCommand | null;
    }
  | {
      readonly kind: "wheel";
      readonly point: DisplayPoint | null;
      readonly deltaX: number;
      readonly deltaY: number;
    };

export interface DesktopInputIntent {
  /** Suppress the host browser's default for this event. */
  readonly preventDefault: boolean;
  /** Move DOM focus to the surface so the keyboard follows the pointer. */
  readonly focusSurface: boolean;
  /**
   * The press origin to store, or `undefined` to leave whatever is there.
   * Distinguishing "set to null" from "don't touch" matters: a release while
   * merely watching must not clear an origin recorded while controlling.
   */
  readonly pressOrigin?: DisplayPoint | null;
  /** What to send to the runtime, or null to send nothing. */
  readonly command: ComputerCommand | null;
  /** Hand control back to the page (Escape). */
  readonly releaseControl: boolean;
}

/** Consume the event, change nothing. */
const IGNORE: DesktopInputIntent = {
  preventDefault: false,
  focusSurface: false,
  command: null,
  releaseControl: false,
};

/**
 * Escape is always local and never forwarded: it is the only way out of a
 * surface that is swallowing every other key, so it cannot be a key the
 * runtime might consume instead.
 */
const RELEASE_KEY = "Escape";

export function desktopInputIntent(input: {
  readonly controlling: boolean;
  readonly gesture: DesktopGesture;
}): DesktopInputIntent {
  // The whole point of this module. Everything below assumes control is held.
  if (!input.controlling) return IGNORE;

  const gesture = input.gesture;
  switch (gesture.kind) {
    case "pointer_down": {
      if (gesture.point === null) return IGNORE;
      return {
        preventDefault: true,
        focusSurface: true,
        pressOrigin: gesture.point,
        command: null,
        releaseControl: false,
      };
    }
    case "pointer_up": {
      // Cleared even when the release produces nothing, so a press that ended
      // in the letterbox cannot pair with the next unrelated release.
      const cleared = { pressOrigin: null, focusSurface: false, releaseControl: false } as const;
      if (gesture.origin === null || gesture.point === null || gesture.button === null) {
        return { ...cleared, preventDefault: false, command: null };
      }
      return {
        ...cleared,
        preventDefault: true,
        command: commandForPointerRelease(gesture.origin, gesture.point, gesture.button),
      };
    }
    case "double_click": {
      if (gesture.point === null) return IGNORE;
      return {
        preventDefault: true,
        focusSurface: false,
        command: { kind: "double_click", x: gesture.point.x, y: gesture.point.y, button: "left" },
        releaseControl: false,
      };
    }
    case "context_menu": {
      // Suppress the host menu whenever we own the pointer, so a right-click
      // reaches the runtime instead of opening Chrome's menu over the video.
      return { ...IGNORE, preventDefault: true };
    }
    case "key_down": {
      if (gesture.key === RELEASE_KEY) {
        return { ...IGNORE, preventDefault: true, releaseControl: true };
      }
      if (gesture.command === null) return IGNORE;
      return { ...IGNORE, preventDefault: true, command: gesture.command };
    }
    case "wheel": {
      if (gesture.point === null) return IGNORE;
      // No preventDefault: the runtime scrolls, and the page under the panel
      // should still behave normally if the command turns out to be a no-op.
      return {
        ...IGNORE,
        command: scrollCommandFor(gesture.point, gesture.deltaX, gesture.deltaY),
      };
    }
  }
}
